import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_TIPO_MARCACAO,
  ehTipoOperante,
  pagamentoEmAberto,
  temCobrancaNaAgenda,
  zAlteracaoDeMarcacao,
  zCancelamentoDeMarcacao,
  type DetalheDaMarcacao,
  type EscopoDeAlteracao,
  type GradeDoDia,
} from '@arena/shared/schemas';
import {
  dataValida,
  hojeNoFuso,
  horaNoRelogio,
  minutosNaGrade,
  naGrade,
  noCalendario,
  partesNoFuso,
} from '@arena/shared/tempo';
import { agendaApi } from '../api/agenda.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
// 10 · turmas
import { AlunosDaAula } from '../turmas/AlunosDaAula.jsx';
import { ModalDeChamada } from '../turmas/ModalDeChamada.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Escolhas, Formulario, Mini, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  PRESETS_DE_PAGAMENTO,
  corSolidaDaMarcacao,
  dataCurta,
  dataPorExtenso,
  diasPorExtenso,
  dinheiro,
  horaCurta,
  horaNaArena,
  paraCentavos,
  presetDoPagamento,
  rotuloDeDuracao,
  seloDoPagamento,
  subtituloDaMarcacao,
  telefoneFormatado,
  tituloDaMarcacao,
  type ChaveDePagamento,
} from './formatos.js';
import { janelaDaGrade, trechosOcupados } from './layout.js';
import { ModalDeBaixa } from './ModalDeBaixa.jsx';
import { AvisoDeDisponibilidade, errosPorCampo, primeiraMensagem } from './ModalDeReserva.jsx';
import { SeletorDeHorario } from './SeletorDeHorario.jsx';
import { useAtrasado } from './useAtrasado.js';

/**
 * `#m-evento` — a marcação aberta.
 *
 * Três modos: ver, remarcar e cancelar. Remarcar aqui é o caminho de
 * TECLADO para o que a grade faz arrastando — arrastar não é acessível,
 * e não pode ser a única forma de mover uma reserva.
 *
 * Em ocorrência de série, os dois formulários fazem a pergunta que o
 * protótipo promete: "somente esta aula ou toda a série?". A resposta
 * vai para o servidor como `escopo`; sem ela, ele recusa — não adivinha.
 *
 * Numa aula de turma fixa, o modo de ver traz os "Alunos da turma" e o
 * botão "Fazer chamada" (documento 10) — a chamada é da AULA, e é daqui,
 * do card da grade, que a recepção e o professor chegam nela.
 */

type Modo = 'ver' | 'remarcar' | 'cancelar';

const DURACOES = [30, 60, 90, 120, 150, 180, 240];

const OPCOES_DE_ESCOPO: readonly { valor: EscopoDeAlteracao; rotulo: string }[] = [
  { valor: 'ocorrencia', rotulo: 'Somente esta' },
  { valor: 'serie', rotulo: 'Toda a série, desta em diante' },
];

export function ModalDaMarcacao({
  id,
  grade,
  onFechar,
}: {
  id: string;
  grade: GradeDoDia;
  onFechar: () => void;
}) {
  const consulta = useQuery({
    queryKey: ['agenda', 'marcacao', id],
    queryFn: () => agendaApi.detalhe(id),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Carregando a marcação…">
        <CorpoDeModal>
          <EsqueletoDeCard />
        </CorpoDeModal>
      </Modal>
    );
  }

  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal aberto onFechar={onFechar} titulo="Marcação" largura="estreita">
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
          />
        </CorpoDeModal>
      </Modal>
    );
  }

  return <MarcacaoAberta marcacao={consulta.data} grade={grade} onFechar={onFechar} />;
}

function MarcacaoAberta({
  marcacao: m,
  grade,
  onFechar,
}: {
  marcacao: DetalheDaMarcacao;
  grade: GradeDoDia;
  onFechar: () => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const fuso = grade.fuso;

  // O calendário da marcação, e o mesmo instante na GRADE: a locação da
  // 01:00 de domingo, na arena que fecha às 02:00, é "25:00" de sábado —
  // é nesse dia e nessa hora que o formulário de remarcar a mostra.
  const local = partesNoFuso(m.inicio, fuso);
  const naGradeAtual = naGrade(local.data, local.hora, grade.horarioFuncionamento);
  const atual = {
    quadraId: m.quadraId,
    data: naGradeAtual.dia,
    inicio: naGradeAtual.hora,
    duracao: Math.round((Date.parse(m.fim) - Date.parse(m.inicio)) / 60_000),
    pagamento: presetDoPagamento(m.formaPagamento, m.situacaoPagamento),
    valor: m.valor != null ? String(m.valor / 100).replace('.', ',') : '',
  };
  const cancelada = Boolean(m.canceladaEm);
  // Mover e redimensionar valem para os cinco tipos — a grade é uma só.
  // O CONTEÚDO de turma fixa e de aula experimental é do módulo dono, e
  // o fim da série de uma turma também: aqui se cancela uma aula só.
  const operante = ehTipoOperante(m.tipo);
  const podeRemarcar = pode('agenda', 'editar') && !cancelada;
  const podeCancelar = pode('agenda', 'cancelar') && !cancelada;
  // Os campos de cobrança só existem na projeção completa — e só locação
  // e evento cobram pela agenda.
  const veCobranca = m.valor !== undefined && temCobrancaNaAgenda(m.tipo);
  // Dar baixa é editar a cobrança: mesma ação da matriz, e só onde há
  // cobrança para editar.
  const podeDarBaixa = veCobranca && pode('agenda', 'editar') && !cancelada;
  const emAberto = pagamentoEmAberto(m.situacaoPagamento);

  const [modo, setModo] = useState<Modo>('ver');
  // 10 · turmas — a chamada abre NO LUGAR deste modal, e fechar volta a ele.
  const [fazendoChamada, setFazendoChamada] = useState(false);
  // A baixa também: é um formulário de três campos que não cabe como
  // quarto modo aqui sem transformar este arquivo em tela de tudo.
  const [dandoBaixa, setDandoBaixa] = useState(false);
  const aulaDeTurma = m.tipo === 'TurmaFixa';
  const [escopo, setEscopo] = useState<EscopoDeAlteracao>('ocorrencia');
  const [quadraId, setQuadraId] = useState(atual.quadraId);
  const [data, setData] = useState(atual.data);
  const [inicio, setInicio] = useState(atual.inicio);
  const [duracao, setDuracao] = useState(atual.duracao);
  const [valor, setValor] = useState(atual.valor);
  const [pagamento, setPagamento] = useState<ChaveDePagamento | ''>(atual.pagamento);
  const [motivo, setMotivo] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const janela = janelaDaGrade(grade.horarioFuncionamento);
  const mudouQuando =
    quadraId !== atual.quadraId ||
    data !== atual.data ||
    inicio !== atual.inicio ||
    duracao !== atual.duracao;
  const serie = Boolean(m.serieId);
  // Para a API, calendário — ver `noCalendario`.
  const destino = dataValida(data) ? noCalendario(data, inicio) : { data, hora: inicio };

  // Os horários da quadra no dia escolhido, sem a própria marcação: ela
  // não colide consigo mesma. Outro dia vem pelo mesmo cache da agenda.
  const outroDia = modo === 'remarcar' && data !== grade.data && dataValida(data);
  const gradeDeOutroDia = useQuery({
    queryKey: ['agenda', 'grade', data],
    queryFn: () => agendaApi.grade(data),
    enabled: outroDia,
  });
  const marcacoesDoDia = outroDia ? (gradeDeOutroDia.data?.marcacoes ?? []) : grade.marcacoes;
  const ocupados = dataValida(data)
    ? trechosOcupados(marcacoesDoDia, quadraId, data, fuso, m.id)
    : [];
  const agora = dataValida(data) ? minutosNaGrade(new Date(), data, fuso) : -Infinity;

  // A verificação ao vivo, para mover UMA ocorrência. "Toda a série" é
  // conferida data a data no servidor, ao salvar.
  const consulta = useAtrasado(
    { quadraId, data: destino.data, inicio: destino.hora, duracaoMinutos: duracao, ignorar: m.id },
    350,
  );
  const disponibilidade = useQuery({
    queryKey: ['agenda', 'disponibilidade', consulta],
    queryFn: () => agendaApi.disponibilidade(consulta),
    enabled:
      modo === 'remarcar' && mudouQuando && (!serie || escopo === 'ocorrencia') && dataValida(data),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });

  const alterar = useMutation({
    mutationFn: (dados: unknown) => agendaApi.alterar(m.id, zAlteracaoDeMarcacao.parse(dados)),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agenda'] }),
  });
  const cancelar = useMutation({
    mutationFn: (dados: unknown) => agendaApi.cancelar(m.id, zCancelamentoDeMarcacao.parse(dados)),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agenda'] }),
  });

  const trocarModo = (novo: Modo) => {
    setErros({});
    setErroGeral(null);
    setModo(novo);
  };

  async function salvarRemarcacao() {
    setErros({});
    setErroGeral(null);

    const dados: Record<string, unknown> = {};
    if (quadraId !== atual.quadraId) dados['quadraId'] = quadraId;
    if (destino.data !== local.data) dados['data'] = destino.data;
    if (destino.hora !== local.hora) dados['inicio'] = destino.hora;
    if (duracao !== atual.duracao) dados['duracaoMinutos'] = duracao;

    if (veCobranca) {
      if (valor !== atual.valor) {
        const centavos = valor.trim() ? paraCentavos(valor) : null;
        if (valor.trim() && centavos === null) {
          setErros({ valor: 'Valor inválido — use 120 ou 120,50.' });
          return;
        }
        dados['valor'] = centavos;
      }
      if (pagamento !== atual.pagamento) {
        const preset = PRESETS_DE_PAGAMENTO.find((p) => p.chave === pagamento);
        dados['formaPagamento'] = preset?.forma ?? null;
        dados['situacaoPagamento'] = preset?.situacao ?? null;
      }
    }

    if (Object.keys(dados).length === 0) {
      setErroGeral('Nada mudou. Escolha outro horário, outra quadra ou outro valor.');
      return;
    }
    if (serie) dados['escopo'] = escopo;

    const pedido = zAlteracaoDeMarcacao.safeParse(dados);
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }

    try {
      const resultado = await alterar.mutateAsync(pedido.data);
      avisar(
        resultado.alteradas > 1
          ? `Série alterada: ${resultado.alteradas} datas daqui em diante.`
          : 'Marcação alterada.',
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  async function confirmarCancelamento() {
    setErros({});
    setErroGeral(null);

    const pedido = zCancelamentoDeMarcacao.safeParse({
      motivo,
      ...(serie ? { escopo: operante ? escopo : 'ocorrencia' } : {}),
    });
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }

    try {
      const resultado = await cancelar.mutateAsync(pedido.data);
      avisar(
        resultado.alteradas > 1
          ? `${resultado.alteradas} datas canceladas e a série encerrada. Os horários ficaram livres.`
          : 'Marcação cancelada. O horário ficou livre.',
      );
      onFechar();
    } catch (erro) {
      setErroGeral(mensagemDoErro(erro));
    }
  }

  // 10 · turmas — depois de todos os hooks: trocar de modal não pode mudar
  // a ordem em que eles são chamados.
  if (fazendoChamada) {
    return <ModalDeChamada marcacaoId={m.id} onFechar={() => setFazendoChamada(false)} />;
  }

  if (dandoBaixa) {
    return (
      <ModalDeBaixa
        marcacaoId={m.id}
        hoje={hojeNoFuso(fuso)}
        fuso={fuso}
        onFechar={() => setDandoBaixa(false)}
      />
    );
  }

  const inicioFormatado = horaCurta(local.hora);
  const fimFormatado = horaCurta(horaNaArena(m.fim, fuso));
  const selo = seloDoPagamento(m.formaPagamento, m.situacaoPagamento);
  const subtitulo = subtituloDaMarcacao(m);
  // A data de criação também no fuso da arena: cortar o ISO daria a
  // data em UTC, que depois das 21h já é o dia seguinte.
  const criadaEm = m.criadaEm ? partesNoFuso(m.criadaEm, fuso).data : null;

  const rodape =
    modo === 'ver' ? (
      <>
        <span className="left">
          {criadaEm ? `Criada em ${dataCurta(criadaEm)}/${criadaEm.slice(0, 4)}` : ''}
        </span>
        <Botao variante="fantasma" onClick={onFechar}>
          Fechar
        </Botao>
        {podeCancelar && (
          <Botao variante="contorno" onClick={() => trocarModo('cancelar')}>
            Cancelar marcação
          </Botao>
        )}
        {podeRemarcar && (
          <Botao
            variante={aulaDeTurma || podeDarBaixa ? 'contorno' : 'primario'}
            onClick={() => trocarModo('remarcar')}
          >
            Remarcar
          </Botao>
        )}
        {/* A baixa é a ação do dia: o card que a recepção abre às 21h é
            o da locação que acabou, e o que ela quer é marcar que
            recebeu. Primária quando há o que cobrar. */}
        {podeDarBaixa && (
          <Botao variante={emAberto ? 'primario' : 'contorno'} onClick={() => setDandoBaixa(true)}>
            {emAberto ? 'Dar baixa' : 'Corrigir pagamento'}
          </Botao>
        )}
        {/* 10 · turmas */}
        {aulaDeTurma && !cancelada && pode('turmas', 'ler') && (
          <Botao onClick={() => setFazendoChamada(true)}>
            {pode('turmas', 'registrar_chamada') ? 'Fazer chamada' : 'Ver chamada'}
          </Botao>
        )}
      </>
    ) : (
      <>
        <span className="left">
          {modo === 'remarcar' ? 'A agenda revalida conflito ao salvar' : 'A linha fica registrada'}
        </span>
        <Botao variante="fantasma" onClick={() => trocarModo('ver')}>
          Voltar
        </Botao>
        {modo === 'remarcar' ? (
          <Botao onClick={() => void salvarRemarcacao()} disabled={alterar.isPending}>
            {alterar.isPending ? 'Salvando…' : 'Salvar alteração'}
          </Botao>
        ) : (
          <Botao onClick={() => void confirmarCancelamento()} disabled={cancelar.isPending}>
            {cancelar.isPending ? 'Cancelando…' : 'Confirmar cancelamento'}
          </Botao>
        )}
      </>
    );

  return (
    <Modal
      aberto
      onFechar={onFechar}
      marca={
        <span
          className="chip"
          style={{ background: corSolidaDaMarcacao(m, m.quadra), color: 'var(--on-ac)' }}
          aria-hidden="true"
        >
          <Icone nome={m.tipo === 'Bloqueio' ? 'lock' : 'ball'} />
        </span>
      }
      titulo={`${tituloDaMarcacao(m)} · ${inicioFormatado} às ${fimFormatado}`}
      descricao={[m.quadra.nome, subtitulo].filter(Boolean).join(' · ')}
      rodape={rodape}
    >
      <CorpoDeModal>
        {modo === 'ver' && (
          <>
            <Mini
              itens={[
                {
                  valor: `${inicioFormatado} às ${fimFormatado}`,
                  rotulo: rotuloDeDuracao(atual.duracao),
                },
                { valor: m.pessoas ?? '—', rotulo: 'pessoas' },
                veCobranca
                  ? {
                      valor: m.valor != null ? dinheiro(m.valor) : '—',
                      rotulo: selo ?? 'pagamento a definir',
                    }
                  : { valor: ROTULO_TIPO_MARCACAO[m.tipo], rotulo: 'tipo' },
              ]}
            />

            <dl className="detalhes">
              <dt>Dia</dt>
              <dd>
                {dataPorExtenso(local.data, true)}
                {atual.data !== local.data &&
                  ` · madrugada, na grade de ${dataPorExtenso(atual.data)}`}
              </dd>
              <dt>Quadra</dt>
              <dd>{m.quadra.nome}</dd>
              <dt>Tipo</dt>
              <dd>{ROTULO_TIPO_MARCACAO[m.tipo]}</dd>
              {veCobranca && (
                <>
                  <dt>Pagamento</dt>
                  <dd>
                    {selo ?? 'a definir'}
                    {m.pagoEm ? ` · recebido em ${dataCurta(m.pagoEm)}` : ''}
                    {emAberto && m.valor != null ? ` · ${dinheiro(m.valor)} a receber` : ''}
                  </dd>
                </>
              )}
              {m.responsavelNome && (
                <>
                  <dt>Responsável</dt>
                  <dd>
                    {m.responsavelNome}
                    {m.responsavelTelefone ? ` · ${telefoneFormatado(m.responsavelTelefone)}` : ''}
                  </dd>
                </>
              )}
              {m.observacao && (
                <>
                  <dt>Observação</dt>
                  <dd>{m.observacao}</dd>
                </>
              )}
            </dl>

            {m.serie && (
              <div className="aviso">
                <Icone nome="cal" />
                <div>
                  <b>Série semanal.</b> {diasPorExtenso(m.serie.diasSemana)} às {m.serie.horaInicio}
                  , desde {dataCurta(m.serie.vigenciaInicio)}
                  {m.serie.vigenciaFim ? ` até ${dataCurta(m.serie.vigenciaFim)}` : ''}
                  {m.serie.encerradaEm ? ' — encerrada' : ''}.
                  {m.excecaoDe &&
                    ` Esta data foi remarcada individualmente: era às ${horaNaArena(m.excecaoDe, fuso)}.`}
                </div>
              </div>
            )}

            {/* 10 · turmas */}
            {aulaDeTurma && pode('turmas', 'ler') && <AlunosDaAula marcacaoId={m.id} />}

            {m.serie && !cancelada && (
              <Nota>
                Ao mover este card para outro horário, o sistema pergunta se a alteração vale{' '}
                <b>somente para esta aula</b> ou para <b>toda a série</b>.
              </Nota>
            )}

            {cancelada && (
              <div className="aviso conflito">
                <Icone nome="x" />
                <div>
                  <b>Cancelada</b> em {dataCurta(partesNoFuso(m.canceladaEm!, fuso).data)} às{' '}
                  {horaNaArena(m.canceladaEm!, fuso)}
                  {m.motivoCancelamento ? ` · ${m.motivoCancelamento}` : ''}
                </div>
              </div>
            )}
          </>
        )}

        {modo === 'remarcar' && (
          <>
            {serie && (
              <Campo rotulo="A alteração vale para" erro={erros['escopo']}>
                <Escolhas
                  aria-label="A alteração vale para"
                  opcoes={OPCOES_DE_ESCOPO}
                  valor={escopo}
                  onChange={(novo) => {
                    setEscopo(novo);
                    // Toda a série não muda de dia da semana — ver o servidor.
                    if (novo === 'serie') setData(atual.data);
                  }}
                />
              </Campo>
            )}

            <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
              <Campo rotulo="Quadra" erro={erros['quadraId']}>
                <select value={quadraId} onChange={(e) => setQuadraId(e.target.value)}>
                  {grade.quadras.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.nome} · {q.superficie}
                    </option>
                  ))}
                </select>
              </Campo>
              <Campo
                rotulo="Data"
                ajuda={
                  serie && escopo === 'serie' ? 'A série mantém os dias da semana.' : undefined
                }
                erro={erros['data']}
              >
                <input
                  type="date"
                  value={data}
                  disabled={serie && escopo === 'serie'}
                  onChange={(e) => setData(e.target.value)}
                />
              </Campo>
              <Campo rotulo="Duração" erro={erros['duracaoMinutos']}>
                <select value={duracao} onChange={(e) => setDuracao(Number(e.target.value))}>
                  {[...new Set([...DURACOES, atual.duracao])]
                    .sort((a, b) => a - b)
                    .map((minutos) => (
                      <option key={minutos} value={minutos}>
                        {rotuloDeDuracao(minutos)}
                      </option>
                    ))}
                </select>
              </Campo>
              {veCobranca && (
                <>
                  <Campo rotulo="Valor" erro={erros['valor']}>
                    <input
                      inputMode="decimal"
                      placeholder="R$ 120,00"
                      value={valor}
                      onChange={(e) => setValor(e.target.value)}
                    />
                  </Campo>
                  <Campo rotulo="Pagamento" erro={erros['situacaoPagamento']}>
                    <select
                      value={pagamento}
                      onChange={(e) => setPagamento(e.target.value as ChaveDePagamento | '')}
                    >
                      <option value="">Ainda não definido</option>
                      {PRESETS_DE_PAGAMENTO.map((p) => (
                        <option key={p.chave} value={p.chave}>
                          {p.rotulo}
                        </option>
                      ))}
                    </select>
                  </Campo>
                </>
              )}
            </Formulario>

            <Campo
              rotulo={`Horário de início · ${horaCurta(horaNoRelogio(inicio))}`}
              ajuda={
                destino.data !== data && dataValida(destino.data)
                  ? `Madrugada: fica em ${dataPorExtenso(destino.data)}, às ${destino.hora} — na grade deste dia.`
                  : 'Riscado: ocupado nesta quadra com essa duração.'
              }
              erro={erros['inicio']}
            >
              <SeletorDeHorario
                aria-label="Horário de início"
                janela={janela}
                valor={inicio}
                duracaoMinutos={duracao}
                ocupados={ocupados}
                agora={agora}
                onChange={setInicio}
              />
            </Campo>

            <div aria-live="polite">
              {mudouQuando && (!serie || escopo === 'ocorrencia') && (
                <AvisoDeDisponibilidade resultado={disponibilidade.data} />
              )}
            </div>
          </>
        )}

        {modo === 'cancelar' && (
          <>
            {serie && !operante && (
              <Nota>
                Aqui se cancela <b>somente esta aula</b>. A série de uma turma fixa acaba na tela de
                Turmas, junto com a turma.
              </Nota>
            )}
            {serie && operante && (
              <Campo rotulo="Cancelar" erro={erros['escopo']}>
                <Escolhas
                  aria-label="Cancelar"
                  opcoes={[
                    { valor: 'ocorrencia', rotulo: 'Somente esta' },
                    { valor: 'serie', rotulo: 'Esta e as seguintes — encerra a série' },
                  ]}
                  valor={escopo}
                  onChange={setEscopo}
                />
              </Campo>
            )}
            <Campo rotulo="Motivo" erro={erros['motivo']}>
              <textarea
                rows={3}
                value={motivo}
                placeholder="Chuva forte, desistência, remarcou para outro dia…"
                onChange={(e) => setMotivo(e.target.value)}
              />
            </Campo>
            <p className="sub">
              A marcação não é apagada: fica registrada como cancelada, com o motivo, e o horário
              fica livre para outra reserva.
            </p>
          </>
        )}

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
